/**
 * Settings: its tabs and their URLs (AJ 2026-10-01, on where a customer
 * maintains the numbers they type in: "on each report respectively, or on its
 * own tab, perhaps in settings". Both: one Inputs tab under Settings, and
 * every input stays editable on its own report).
 *
 *   /<slug>/Settings          Preferences (the page as it was)
 *   /<slug>/Settings/Inputs   Inputs: every typed number, grouped by report
 *
 * A section of the Inputs tab is addressed by a URL fragment
 * (/<slug>/Settings/Inputs#disposal), which is what each report's "All
 * inputs" link opens.
 *
 * Plain .mjs so node:test covers the path math without React (the
 * navSections.mjs precedent). The shell, the Settings page and the reports'
 * links all read the paths from here, so they cannot drift apart.
 */

/** The Settings route segment under /<slug>/. */
export const SETTINGS_ROUTE = "Settings";

export const SETTINGS_TABS = Object.freeze([
  Object.freeze({ id: "preferences", label: "Preferences", segment: "" }),
  Object.freeze({ id: "inputs", label: "Inputs", segment: "Inputs" }),
]);

/** The tab a client lands on at /<slug>/Settings. */
export const DEFAULT_SETTINGS_TAB = "preferences";

function tabById(tabId) {
  return SETTINGS_TABS.find((t) => t.id === tabId) ?? SETTINGS_TABS[0];
}

/** /<slug>/Settings for Preferences, /<slug>/Settings/Inputs for Inputs. */
export function settingsTabPath(slug, tabId = DEFAULT_SETTINGS_TAB) {
  const tab = tabById(tabId);
  const base = `/${slug ?? ""}/${SETTINGS_ROUTE}`;
  return tab.segment ? `${base}/${tab.segment}` : base;
}

/**
 * The Settings tab a pathname names: the segment after /<slug>/Settings,
 * matched without regard to case; anything else (no segment, an unknown one,
 * another route) is Preferences.
 *
 * @param {string | null | undefined} pathname
 * @param {string | null | undefined} slug
 * @returns {"preferences" | "inputs"}
 */
export function settingsTabFromPath(pathname, slug) {
  const parts = String(pathname ?? "")
    .split("/")
    .filter(Boolean)
    .map((p) => {
      try {
        return decodeURIComponent(p);
      } catch {
        return p;
      }
    });
  const at = slug ? parts.indexOf(slug) : -1;
  const rest = at >= 0 ? parts.slice(at + 1) : parts;
  if ((rest[0] ?? "").toLowerCase() !== SETTINGS_ROUTE.toLowerCase()) return DEFAULT_SETTINGS_TAB;
  const segment = (rest[1] ?? "").toLowerCase();
  const tab = SETTINGS_TABS.find((t) => t.segment && t.segment.toLowerCase() === segment);
  return tab ? tab.id : DEFAULT_SETTINGS_TAB;
}

/**
 * The Inputs tab's sections, in page order. `report` is the report id a
 * manifest tab names (`report: disposal`); the Executive section has none
 * because the Executive page is not a manifest tab. `anchor` is the URL
 * fragment and the section element's id.
 */
export const INPUT_SECTIONS = Object.freeze([
  Object.freeze({ id: "disposal", anchor: "disposal", report: "disposal" }),
  Object.freeze({
    id: "route_profitability",
    anchor: "route-profitability",
    report: "route_profitability",
  }),
  Object.freeze({ id: "containers", anchor: "containers", report: "containers" }),
  Object.freeze({ id: "ro_efficiency", anchor: "ro-efficiency", report: "ro_efficiency" }),
  Object.freeze({ id: "executive", anchor: "executive", report: null }),
]);

/** The Inputs tab, or one section of it (a section id from INPUT_SECTIONS). */
export function inputsPath(slug, sectionId) {
  const base = settingsTabPath(slug, "inputs");
  const section = INPUT_SECTIONS.find((s) => s.id === sectionId);
  return section ? `${base}#${section.anchor}` : base;
}
